import { useRouter } from "next/router";
import React, { useEffect, useRef } from "react";

import { useAdminApi } from "@/custom-hooks/apis/use-admin-api";
import { useAuthApi } from "@/custom-hooks/apis/use-auth-api";
import { useColormeApi } from "@/custom-hooks/apis/use-colorme-api";
import { useCorporateApi } from "@/custom-hooks/apis/use-corporate-api";
import { COLORME_CALLBACK_PATH, COLORME_INSTALLED_PATH, MYPAGE_COLORME_LINKED_PATH } from "@/global-state/app-path";
import { ADMIN_LOGIN_PATH, CORPORATE_LOGIN_PATH } from "@/global-state/external-path";
import { authUserAtom, idTokenAtom, initializingAtom, refererAtom } from "@/global-state/jotai-atoms";
import { clearColormePendingLink, readColormePendingLink } from "@/utils/colorme-pending-link";

import InitialScreenContainer from "@/components/common/InitialScreenContainer";
import QueryProgress from "@/components/common/QueryProgress";
import BackdropProgress from "@/components/provider/BackdropProgress";
import { useAtom, useSetAtom } from "jotai";

type Props = {
  children: React.ReactNode;
};

const AuthProvider: React.FC<Props> = (props) => {
  const router = useRouter();
  const setReferer = useSetAtom(refererAtom);
  const referer = router.query.referer || router.query.referrer || router.query.ref;

  const [initializing, setInitializing] = useAtom(initializingAtom);
  const [idToken] = useAtom(idTokenAtom);
  const [authUser] = useAtom(authUserAtom);
  const { authSync } = useAuthApi();
  const { adminAuthSync } = useAdminApi();
  const { corporateAuthSync } = useCorporateApi();
  const { linkColormeByTicket } = useColormeApi();
  // 保留中のカラーミーショップ連携を一度だけ消化するためのフラグ
  const colormeLinkRef = useRef(false);

  // NOTE: 初回呼び出し
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      console.log("🔍 Debug - AuthProvider: APP_ENV =", process.env.APP_ENV);
      console.log("🔍 Debug - AuthProvider: DEV_ENV =", process.env.DEV_ENV);

      if (process.env.APP_ENV === "corporate") {
        console.log("🔍 Debug - AuthProvider: Branch = CORPORATE");
        console.log("🔍 Debug - AuthProvider: Calling corporateAuthSync...");
        await corporateAuthSync();
        await setInitializing(false);
        return;
      }
      if (process.env.APP_ENV === "admin") {
        console.log("🔍 Debug - AuthProvider: Branch = ADMIN");
        if (process.env.DEV_ENV === "admin-local") {
          await adminAuthSync();
        }
        await router.push(ADMIN_LOGIN_PATH);
        await setInitializing(false);
      } else {
        console.log("🔍 Debug - AuthProvider: Branch = OTHER (local/staging/production)");
        if (initializing) {
          // リロード後もログイン状態を保つため、memberMe まで走らせて authUser を復元する
          const u = await authSync(false);
          if (cancelled) u?.();
          else unsubscribe = u;
        }
      }
    })();
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (authUser && idToken) {
      let unsubscribe: (() => void) | undefined;
      let cancelled = false;
      // 定期リフレッシュはトークンだけ更新する(refresh=true)。
      // member-me は Stripe まで見に行くので、ページロード時の1回に留める。
      const timeout = setTimeout(async () => {
        const u = await authSync(true);
        if (cancelled) u?.();
        else unsubscribe = u;
      }, 1000 * 60 * 5);
      return () => {
        cancelled = true;
        clearTimeout(timeout);
        unsubscribe?.();
      };
    }
  }, [idToken, authUser?.firebase_auth_user_id]);

  useEffect(() => {
    if (referer) {
      setReferer(referer as string);
    }
  }, [referer]);

  // カラーミーショップのアプリを追加した店主が、アカウントを作る／ログインし終えた瞬間に
  // 紐付けまで案内する。マイページを自力で探させないための処理。
  // カラーミーショップの認証がまだなら、いきなり認可画面へ飛ばさず設定ページに戻して
  // 「カラーミーショップにログインしてつなぐ」を押してもらう（HINOMEにログインした直後に
  // 説明なくカラーミーショップのログイン画面が出ると、店主には理由が分からないため）。
  // コールバック処理中のページでは二重に走らせない。
  useEffect(() => {
    if (!authUser || colormeLinkRef.current) return;
    if (router.pathname === COLORME_CALLBACK_PATH) return;
    const pending = readColormePendingLink();
    if (!pending) return;
    colormeLinkRef.current = true;
    (async () => {
      if (pending.linkTicket) {
        // カラーミーショップの認証は済んでいるので、引換券だけで紐付ける
        const res = await linkColormeByTicket(pending.linkTicket);
        clearColormePendingLink();
        if (res) {
          await router.push(MYPAGE_COLORME_LINKED_PATH);
          return;
        }
        // 引換券が切れていたら、下の「認証がまだ」と同じく設定ページで案内する
      }
      clearColormePendingLink();
      if (pending.shopId) {
        await router.push(`${COLORME_INSTALLED_PATH}?shop=${encodeURIComponent(pending.shopId)}&step=link`);
      } else {
        await router.push(MYPAGE_COLORME_LINKED_PATH);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.firebase_auth_user_id]);

  if (initializing) {
    return <InitialScreenContainer />;
  }

  if (process.env.APP_ENV === "admin" && authUser?.id && !authUser?.is_admin) {
    return <QueryProgress />;
  }

  return <>{props.children}</>;
};

export default AuthProvider;
